import React from 'react'
import Slider from 'react-slick'
import classNames from 'classnames'
import { Trans } from '@lingui/macro'
import { Flex } from '@chakra-ui/react'

import { CampaignBanner } from '../campaign-banner/CampaignBanner'
import { PageContentWrapper } from '../../../../components/common/page/Page'

import './HomepageBanner.less'

import { captureEvent } from '@sentry/browser'

import { CampaignAnalyticEventTypeEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import {
  useCampaignBannerHomepageQuery,
  useCaptureBannerAnalyticEventMutation
} from './HomepageBanner.codegen'
import { useLooopTheme } from '../../../../public-api/hooks/useLooopTheme'

const bannerIdsViewsRecorded = new Set()

export const HomepageBanner: React.FC = () => {
  const { theme } = useLooopTheme()
  const { data } = useCampaignBannerHomepageQuery({
    fetchPolicy: 'cache-first'
  })
  const [captureBannerAnalyticEvent] = useCaptureBannerAnalyticEventMutation()

  const activeBannersLength = data?.me.activeBanners.length ?? 0
  const me = data?.me

  if (!data || activeBannersLength === 0) {
    return (
      <PageContentWrapper>
        <Flex className="dashboard-welcome" justifyContent={'space-between'}>
          <h1>
            <Trans>Hello, {me?.first_name || me?.display_name}</Trans>
          </h1>
        </Flex>
      </PageContentWrapper>
    )
  }

  const recordViewEvent = (idx: number) => {
    const banner = data.me.activeBanners[idx]
    if (!bannerIdsViewsRecorded.has(banner.id)) {
      captureBannerAnalyticEvent({
        variables: {
          bannerId: banner.id,
          timestamp: new Date().toISOString(),
          eventType: CampaignAnalyticEventTypeEnum.BANNER_SHOWN
        }
      })
      bannerIdsViewsRecorded.add(banner.id)
    }
  }

  const sliderSettings = {
    dots: data.me.activeBanners.length > 1,
    speed: 800,
    infinite: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 8000,
    fade: true,
    onInit: () => {
      recordViewEvent(0)
    },
    afterChange: (idx: number) => {
      recordViewEvent(idx)
    }
  }

  return (
    <>
      <div
        className={classNames('CampaignBannerHomepage', {
          'multiple-slides': data.me.activeBanners.length > 1
        })}
      >
        <div
          className="CampaignBannerHomepage__inner"
          style={{ backgroundColor: theme.colors.navbarBackground }}
        >
          <PageContentWrapper>
            <Slider {...sliderSettings}>
              {data.me.activeBanners.map((banner) => {
                if (!banner.image) {
                  const message = `missing image in banner ${banner.id}`
                  console.error(message)
                  captureEvent({
                    message,
                    contexts: {
                      banner
                    }
                  })
                  return null
                }
                return (
                  <div key={banner.id}>
                    <CampaignBanner
                      buttonLink={banner.button_link}
                      buttonText={banner.button_caption}
                      onButtonClick={() => {
                        return captureBannerAnalyticEvent({
                          variables: {
                            bannerId: banner.id,
                            timestamp: new Date().toISOString(),
                            eventType:
                              CampaignAnalyticEventTypeEnum.BANNER_CLICKED
                          }
                        })
                      }}
                      text={banner.text}
                      header={banner.header}
                      coverImageSrc={banner.image.cdnUrl as any}
                      coverImageVariants={banner.image.imageVariants as any}
                      coverImageBase64={null}
                    />
                  </div>
                )
              })}
            </Slider>
          </PageContentWrapper>
        </div>
      </div>
      <PageContentWrapper>
        <Flex className="dashboard-welcome" justifyContent={'space-between'}>
          <h1>
            <Trans>Hello, {me?.first_name || me?.display_name}</Trans>
          </h1>
        </Flex>
      </PageContentWrapper>
    </>
  )
}

export default HomepageBanner
